/**
 * 主应用组件
 * Package Receiver 前端应用的根组件
 */

import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  BarChart3, 
  Database, 
  Settings, 
  Menu, 
  X
} from 'lucide-react';
import { clsx } from 'clsx';

// 组件导入
import SearchPanel from './components/SearchPanel';
import ResultsTable from './components/ResultsTable';
import StatsPanel from './components/StatsPanel';
import Card from './components/ui/Card';

// Hooks 导入
import { useBatchDelete, useDeleteSingle, useSearch } from './hooks/useApi';

// 类型导入
import type { SearchResult } from './types';

// 标签页类型
type TabType = 'search' | 'stats' | 'settings';

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('search');
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // 批量删除 Hook
  const { deleteData, loading: deleteLoading, error: deleteError, result: deleteResult } = useBatchDelete();
  
  // 单个记录删除 Hook
  const { deleteRecord, loading: singleDeleteLoading, error: singleDeleteError, result: singleDeleteResult } = useDeleteSingle();
  
  // 搜索 Hook  
  const { search, searchResult } = useSearch();
  
  // 当搜索结果更新时，同步到 searchResults 状态
  useEffect(() => {
    setSearchResults(searchResult);
  }, [searchResult]);

  // 处理搜索结果
  const handleSearchResults = useCallback((results: SearchResult | null) => {
    setSearchResults(results);
  }, []);

  // 处理单个记录删除
  const handleDeleteRecord = useCallback(async (id: string) => {
    if (window.confirm('确定要删除这条记录吗？')) {
      try {
        await deleteRecord(id);
        // 删除成功后立即刷新搜索结果
        setTimeout(() => {
          search({ key: '', value: '', limit: 20, offset: 0 });
        }, 500);
      } catch (error) {
        console.error('删除记录失败:', error);
      }
    }
  }, [deleteRecord, search]);

  // 处理批量删除
  const handleBatchDelete = useCallback(async (key: string, value: string) => {
    if (window.confirm(`确定要删除所有 ${key}="${value}" 的记录吗？`)) {
      await deleteData(key, value);
      // 删除成功后刷新搜索结果
      if (deleteResult) {
        // 可以重新执行搜索或更新结果
      }
    }
  }, [deleteData, deleteResult]);

  // 标签页配置
  const tabs = [
    {
      id: 'search' as TabType,
      name: '数据搜索',
      icon: Search,
      description: '搜索和查看接收到的数据'
    },
    {
      id: 'stats' as TabType,
      name: '统计分析',
      icon: BarChart3,
      description: '查看数据统计和分析图表'
    },
    {
      id: 'settings' as TabType,
      name: '系统设置',
      icon: Settings,
      description: '配置系统参数和选项'
    }
  ];

  // 页面动画变体
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo 和标题 */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 lg:hidden"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <Database className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Package Receiver</h1>
                  <p className="text-xs text-gray-500 hidden sm:block">数据接收与查询平台</p>
                </div>
              </div>
            </div>

            {/* 右侧操作 */}
            <div className="flex items-center space-x-4">
              {/* 状态指示器 */}
              <div className="hidden sm:flex items-center space-x-2 text-sm text-gray-600">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>系统正常</span>
              </div>


            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* 侧边栏 */}
          <aside className={clsx(
            'lg:w-64 lg:flex-shrink-0',
            'lg:block',
            sidebarOpen ? 'block' : 'hidden'
          )}>
            <Card className="sticky top-24">
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setSidebarOpen(false);
                    }}
                    className={clsx(
                      'w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors',
                      activeTab === tab.id
                        ? 'bg-primary-100 text-primary-700 border border-primary-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    )}
                  >
                    <tab.icon className="w-5 h-5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{tab.name}</div>
                      <div className="text-xs text-gray-500 mt-1">{tab.description}</div>
                    </div>
                  </button>
                ))}
              </nav>

              {/* 快速统计 */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-3">快速统计</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">总记录数</span>
                    <span className="font-medium text-gray-900">
                      {searchResults?.total ? searchResults.total.toLocaleString() : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">当前显示</span>
                    <span className="font-medium text-gray-900">
                      {searchResults?.data.length || 0}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </aside>

          {/* 主内容区域 */}
          <main className="flex-1 min-w-0">
            <motion.div
              key={activeTab}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              {activeTab === 'search' && (
                <div className="space-y-6">
                  {/* 搜索面板 */}
                  <SearchPanel onSearchResults={handleSearchResults} />
                  
                  {/* 搜索结果 */}
                  <ResultsTable
                    results={searchResults}
                    onDelete={handleDeleteRecord}
                    onBatchDelete={handleBatchDelete}
                  />
                  
                  {/* 删除状态提示 */}
                  {(deleteLoading || singleDeleteLoading) && (
                    <Card>
                      <div className="text-center py-4">
                        <div className="inline-flex items-center space-x-2 text-sm text-gray-600">
                          <div className="animate-spin w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full"></div>
                          <span>正在删除数据...</span>
                        </div>
                      </div>
                    </Card>
                  )}
                  
                  {(deleteError || singleDeleteError) && (
                    <Card>
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600">删除失败: {deleteError || singleDeleteError}</p>
                      </div>
                    </Card>
                  )}
                  
                  {(deleteResult || singleDeleteResult) && (
                    <Card>
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-600">
                          {deleteResult?.message || (singleDeleteResult ? '记录删除成功' : '')}
                        </p>
                      </div>
                    </Card>
                  )}
                </div>
              )}

              {activeTab === 'stats' && (
                <StatsPanel />
              )}

              {activeTab === 'settings' && (
                <Card title="系统设置" subtitle="配置应用参数和选项">
                  <div className="space-y-6">
                    <div className="text-center py-12">
                      <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">设置功能开发中</h3>
                      <p className="text-gray-500">更多配置选项即将推出</p>
                    </div>
                  </div>
                </Card>
              )}
            </motion.div>
          </main>
        </div>
      </div>

      {/* 页脚 */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <div className="text-sm text-gray-500">
              Developed by Season
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;